import { floorplannerStore } from "../../store/floorplannerStore";
import { editorStore } from "../../store/editorStore";
import { RulerLineType, SingleLineType, WallType } from "../../types/floorplannerTypes";
import * as THREE from 'three';

const getControlHandleSnapTolerance = (object: WallType | SingleLineType | RulerLineType): number => {
    if (object.type === "wall") {
        return Math.max((object.wallWidth ?? floorplannerStore.wallWidth) / 2, 0.01);
    }

    return 0.05;
};

/**
 * Drag control handle of ARC object, dragging the visual midpoint of the curve.
 * Calculates the required Bezier control point to pass through the dragged midpoint.
 */
export const dragControlHandle = (
    source: WallType | SingleLineType | RulerLineType,
    newX: number,
    newY: number,
) => {
    const object = floorplannerStore.findObjectId(source.id);
    if (!object) return;

    const midPoint = new THREE.Vector2(
        (object.start.x + object.end.x) / 2,
        (object.start.y + object.end.y) / 2,
    );

    if (!object.controlPoint) {
        object.controlPoint = midPoint.clone();
    }

    const curPoint = new THREE.Vector2(newX, newY);
    const wallDirection = new THREE.Vector2().subVectors(object.end, object.start).normalize();
    const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);

    let targetM = curPoint.clone();

    if (!editorStore.isShiftPressed) {
        // Default mode snaps to 0, 45, and 90 degrees relative to the chord.
        const vectorToM = targetM.clone().sub(midPoint);
        const distance = vectorToM.length();
        if (distance > 0.01) {
            let angle = Math.atan2(vectorToM.dot(perpendicular), vectorToM.dot(wallDirection));
            const snapInterval = Math.PI / 4;
            const snappedAngle = Math.round(angle / snapInterval) * snapInterval;
            const snDirX = Math.cos(snappedAngle);
            const snDirY = Math.sin(snappedAngle);
            const snappedVector = wallDirection.clone().multiplyScalar(snDirX)
                .add(perpendicular.clone().multiplyScalar(snDirY))
                .multiplyScalar(distance);
                
            targetM = midPoint.clone().add(snappedVector);

            if (Math.abs(snDirY) < 0.01) {
                targetM = midPoint.clone();
            }
        } else {
            targetM = midPoint.clone();
        }
    } else {
        // Shift temporarily disables angle snapping for free dragging.
        const newDistance = new THREE.Vector2(targetM.x - midPoint.x, targetM.y - midPoint.y).dot(perpendicular);
        const snapPoint = midPoint.clone().add(perpendicular.clone().multiplyScalar(newDistance));
        const snapTolerance = getControlHandleSnapTolerance(object);
        
        if (snapPoint.distanceTo(targetM) <= snapTolerance) {
            targetM = snapPoint;
        }
        
        // Also snap to straight wall if it's very close to the chord
        if (targetM.distanceTo(midPoint) <= snapTolerance / 4) {
            targetM = midPoint.clone();
        }
    }

    // Calculate new control point P1 such that the curve passes through targetM at t=0.5
    // targetM = 0.25 * P0 + 0.5 * P1 + 0.25 * P2
    // P1 = 2 * targetM - 0.5 * (P0 + P2)
    const p0 = object.start;
    const p2 = object.end;
    
    // Check if the wall is effectively straight
    if (targetM.distanceTo(midPoint) < 0.01) {
        if (object.lineForm === "arc") {
            floorplannerStore.setObjectProperty(object.id, 'lineForm', 'straight');
            floorplannerStore.setObjectProperty(object.id, 'controlPoint', midPoint.clone());
        }
    } else {
        if (object.lineForm !== "arc") {
            floorplannerStore.setObjectProperty(object.id, 'lineForm', 'arc');
        }
        const newControl = targetM.clone().multiplyScalar(2).sub(
            p0.clone().add(p2).multiplyScalar(0.5)
        );
        floorplannerStore.setObjectProperty(object.id, 'controlPoint', newControl);
    }
}
